// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import Experience from 'app/common/react/components/experiences/Experience/Experience';
import getBoardOrder from 'app/common/react/components/profile/ProfileBoards/utils/getBoardOrder';
import i18n from 'app/common/lib/i18n';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import type { BoardSortOptionsType } from 'app/packages/pinterest-flow-types/user';
import type { State as ReduxState } from 'app/common/redux/types';
import withResource from 'app/common/lib/react/withResource';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { track } from 'app/common/lib/react/analytics';
import { updateBoardSortOption } from 'app/common/redux/actions/views';
import { Box, Label, SelectList, Text, Flyout } from 'gestalt';

type State = {|
  selectedSortOption?: BoardSortOptionsType,
|};

type Option = {
  label: string,
  value: string,
};

type Props = {
  dispatch: Function,
  hasCustomOrder: boolean,
  isOwnProfile: boolean,
  metadata: {
    data: {
      most_recent_board_sort_order: ?BoardSortOptionsType,
    },
  },
  updatedBoardOrder?: BoardSortOptionsType,
  viewer: {
    username: string,
  },
};

class BoardSortSelectlist extends Component<Props, State> {
  state = {
    selectedSortOption: undefined,
  };

  getEducationText = (
    experienceId:
      | PCONST.thrift.Experience.WEB_SORT_BOARD_EDUCATION_TOOLTIP
      | PCONST.thrift.Experience.WEB_PROFILE_MIX_PUBLIC_AND_SECRET_BOARDS,
  ) => {
    if (experienceId === PCONST.thrift.Experience.WEB_SORT_BOARD_EDUCATION_TOOLTIP) {
      return i18n._(
        'New! Sort your boards so everything’s easier to find',
        'Label to introduce the Sort Board feature',
      );
    }

    if (experienceId === PCONST.thrift.Experience.WEB_PROFILE_MIX_PUBLIC_AND_SECRET_BOARDS) {
      return i18n._(
        'Your public and secret boards are now displayed together. ' +
          'Use the “drag and drop” sort option to keep your secret boards at the bottom of your profile.',
        'User education for mixing public/secret boards',
      );
    }

    return null;
  };

  anchor: ?HTMLElement;

  completeBoardSortExperience: ?Function;

  handleClickSortBoard = ({ value }: { value: string }) => {
    const selectedSortOption: BoardSortOptionsType = (value: any);

    if (this.completeBoardSortExperience) {
      this.completeBoardSortExperience();
    }

    const { dispatch } = this.props;
    this.setState({ selectedSortOption });
    const options = {
      most_recent_board_sort_order: selectedSortOption,
    };

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.SORT_BOARDS_SELECTLIST,
      element: this.logElementType(selectedSortOption),
    });

    const resource = ResourceFactory.create('UserMetadataResource', options);
    resource.callUpdate();
    dispatch(updateBoardSortOption(selectedSortOption));
  };

  logElementType(value: BoardSortOptionsType) {
    switch (value) {
      case 'alphabetical':
        return PCONST.thrift.ElementType.SORT_BOARDS_ALPHABETICAL_OPTION;
      case 'last_pinned_to':
        return PCONST.thrift.ElementType.SORT_BOARDS_MOST_RECENT_OPTION;
      case 'newest':
        return PCONST.thrift.ElementType.SORT_BOARDS_CREATED_AT_NEWEST_OPTION;
      case 'oldest':
        return PCONST.thrift.ElementType.SORT_BOARDS_CREATED_AT_OLDEST_OPTION;
      case 'custom':
        return PCONST.thrift.ElementType.SORT_BOARDS_CUSTOM_OPTION;
      default:
        return '';
    }
  }

  render() {
    const { isOwnProfile, hasCustomOrder, metadata, updatedBoardOrder } = this.props;
    const anchor = this.anchor;

    if (!metadata || !metadata.data) {
      return null;
    }

    const options: Array<Option> = [
      {
        label: i18n._('Last saved to', 'board sort order option'),
        value: 'last_pinned_to',
      },
      {
        label: i18n._('A to Z', 'alphabetical board sort order option'),
        value: 'alphabetical',
      },
      {
        label: i18n._('Newest', 'board sort order option'),
        value: 'newest',
      },
      {
        label: i18n._('Oldest', 'board sort order option'),
        value: 'oldest',
      },
      {
        label: i18n._('Drag and drop', 'board sort order option for custom reordering'),
        value: 'custom',
      },
    ];

    const boardOrderProps = {
      canSortOwnBoards: true, // this checks is in ProfileTabBar
      hasCustomOrder,
      isOwnProfile: true, // this checks is in ProfileTabBar
      mostRecentBoardSortOrder: metadata.data.most_recent_board_sort_order || undefined,
      updatedBoardOrder,
    };

    const boardOrder = getBoardOrder({ ...boardOrderProps });

    return (
      <Box display="flex" alignItems="center">
        <Box display="visuallyHidden">
          <Label htmlFor="boardSort">
            {i18n._('Sort boards', 'text label for select list that lets users sort their boards')}
          </Label>
        </Box>
        <div
          ref={(el) => {
            this.anchor = el;
          }}
        >
          <Box flex="shrink">
            <SelectList
              id="boardSort"
              onChange={this.handleClickSortBoard}
              options={options}
              value={boardOrder}
            />
          </Box>
        </div>
        {isOwnProfile && (
          <Experience
            placementId={PCONST.thrift.Placement.WEB_USER_PROFILE_TAKEOVER}
            experienceIds={[
              PCONST.thrift.Experience.WEB_SORT_BOARD_EDUCATION_TOOLTIP,
              PCONST.thrift.Experience.WEB_PROFILE_MIX_PUBLIC_AND_SECRET_BOARDS,
            ]}
          >
            {({ complete, dismiss, experience_id }) => {
              this.completeBoardSortExperience = complete;
              return (
                <Box dangerouslySetInlineStyle={{ __style: { zIndex: '100' } }}>
                  <Flyout
                    anchor={anchor}
                    color="blue"
                    idealDirection="down"
                    onDismiss={dismiss}
                    shouldFocus={false}
                    size="sm"
                  >
                    <Box padding={2}>
                      <Text bold color="white" size="md" overflow="normal">
                        {this.getEducationText(experience_id)}
                      </Text>
                    </Box>
                  </Flyout>
                </Box>
              );
            }}
          </Experience>
        )}
      </Box>
    );
  }
}

export default compose(
  withViewer,
  connect<*, *, _, _, _, _>(
    (state: ReduxState): {| updatedBoardOrder?: BoardSortOptionsType |} => ({
      updatedBoardOrder: state.ui.views && state.ui.views.updatedBoardOrder,
    }),
  ),
  withResource({
    name: 'UserMetadataResource',
    key: 'metadata',
    options: (props) => ({
      user: {
        id: props.viewer.username,
      },
    }),
  }),
)(BoardSortSelectlist);
